<template>
  <div class="nuttrema-empty-result" :class="[ theme ? `theme-${theme}` : '' ]">
    <div class="nuttrema-empty-result__center">
      <div class="state-holder">
          <slot v-if="options.image" name="svg">
            <img class="empty-svg" :src="options.image" :style="options.imgStyle">
          </slot>

          <slot v-else name="icon">
            <i class="empty-icon blink-fade" :class="icon" aria-hidden="true"></i>
          </slot>

          <slot name="text">
            <h1 v-if="options.title">{{ $t(options.title) }}</h1>
            <h4 v-if="options.subTitle">{{ $t(options.subTitle) }}</h4>
          </slot>

          <slot name="button"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { size, random, get } from 'lodash';

export default {
  name: 'nuttrema-empty-result',
  props: {
    icon: {
      type: String,
      required: false,
      default: 'fal fa-coffee',
    },
    svg: {
      required: false,
      default: null,
    },
    predefinedSet: {
      required: false,
      default: null,
    },
    title: {
      required: false,
      default: null,
    },
    subTitle: {
      required: false,
      default: null,
    },
    imgStyle: {
      required: false,
      default: null,
    },
    theme: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      sets: {
        landscape: {
          title: 'label.there_are_no_items',
          subTitle: 'label.create_first_item',
          path: '/static/svg/landscape-dariusdan/',
          files: ['cactus.svg', 'directions.svg', 'field.svg', 'nightdesert.svg', 'nighttree.svg', 'rain.svg', 'relax.svg', 'tent.svg', 'tower.svg', 'tree.svg', 'windmill.svg', 'winter.svg'],
        },
        'not-found': {
          title: 'label.item_not_found',
          subTitle: 'label.item_not_found_text',
          path: '/static/svg/not-found/',
          files: ['ghost-404.svg', 'lego-404.svg'],
        },
        forbiden: {
          title: 'label.error_403_title',
          subTitle: 'label.error_403_text',
          path: '/static/svg/forbiden/',
          files: ['ghost-403.svg'],
        },
        'thank-you': {
          title: 'label.thank_you_for_your_purchase',
          subTitle: 'label.your_trust_quote',
          path: '/static/svg/thank-you/',
          files: ['dc-ballon-sun-cloud.svg'],
          imgStyle: { width: '50%' },
        },
      },
      options: {
        image: null,
        title: 'label.list_is_empty',
        subTitle: 'label.create_first_item',
        imgStyle: null,
      },
    };
  },
  methods: {
    applyPredefinedSet(name) {
      const set = get(this.sets, name, []);

      const path = get(set, 'path', []);
      const files = get(set, 'files', []);

      const upper = size(files);
      const index = random(0, upper - 1);

      const file = files[index];
      const title = get(set, 'title');
      const subTitle = get(set, 'subTitle');
      const imgStyle = get(set, 'imgStyle');

      if (file) this.options.image = `${path}/${file}`;
      if (title) this.options.title = title;
      if (subTitle) this.options.subTitle = subTitle;
      if (imgStyle) this.options.imgStyle = imgStyle;
    },
  },
  created() {
    if (this.predefinedSet) this.applyPredefinedSet(this.predefinedSet);
    if (this.svg) this.options.image = this.svg;
    if (this.title) this.options.title = this.title;
    if (this.subTitle) this.options.subTitle = this.subTitle;
    if (this.imgStyle) this.options.imgStyle = this.imgStyle;
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/theme/colors";

.nuttrema-empty-result {
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  flex-grow: 1;
  height: 100%;
  display: flex;
  position: absolute;
  align-self: stretch;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  background-color: rgba(255,255,255,0.75);

  &.with-logo {
    &:after {
      z-index: 0;
      width: 100%;
      content: '';
      height: 100%;
      opacity: 0.035;
      position: absolute;
      background: transparent url('~assets/images/dc-logo-small-gray.svg') no-repeat center center;
      background-size: 115%;
    }
  }

  .nuttrema-empty-result__center {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    z-index: 1;

    .state-holder {
      text-align: center;
      width: 70%;

      > i {
        font-size: 6rem;
        line-height: 8rem;
        color: $gray-light;
        text-shadow: 0 -1px 0 $gray;
      }

      > img.empty-svg {
        width: 36%;
      }

      h1 {
        margin: 0.15em 0;
        font-weight: 700;
        font-size: 2.5rem;
        font-family: 'Asap';
        line-height: 2.5rem;
      }

      h4 {
        margin-bottom: 1em;
        font-size: 1.15rem;
      }
    }
  }

  .nuttrema-theme-professional-topography & {
    background-color: rgba(255,255,255,0.15);
    color: $gray-light;
  }

  .nuttrema-theme-professional-turquoise & {
    background-color: transparent;
  }

  &.theme-transparent-white {
    color: #fff;
    background-color: transparent;
    .nuttrema-empty-result__center {
      .state-holder {
        > i {
          color: #fff;
        }
      }
    }
  }

  &.is-transparent {
    background-color: transparent;
  }
  &.is-zero-z {
    z-index: 0;
  }

  &.is-white {
    background-color: #fff;
  }

  &.is-with-border-radius {
    border-radius: 5px;
  }

  &.in-east-block {
    .nuttrema-empty-result__center {
      .state-holder {
        h1 {
          font-size: 2rem;
          line-height: 2rem;
          margin-bottom: 0.5em;
        }
        h4 {
          font-size: 1rem;
          line-height: 1.35;
        }
      }
    }
  }

  &.is-pull-5em {
    .nuttrema-empty-result__center {
      margin-top: -5em;
    }
  }

  &.theme-default {
    color: $gray-light;
    .nuttrema-empty-result__center {
      .state-holder {
        > i {
          color: $gray-light;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/panels/nuttrema-empty-result.vue